import { applyCeiling, ResolvedRoleLevels } from '@blisspointmedia/bpm-types/dist/dashboarding';
import {
  assignableRoleLevels,
  audienceToLevel,
  isRequestAssignable,
  levelToAudience,
  roleLevelLabel,
  SharingRequest
} from './chart-permissions';
import { createDefaultRoleLevels } from './chart-save';

/**
 * What the frontend knows about the record a sharing request names.
 *
 * FOUR states, because each produces a different outcome below and an optional levels object
 * cannot express that: a loading record waits, a never-saved record is refused, and only a governed
 * record can be compared against.
 *
 * A DRAFT chart is deliberately not a state here. Its request is read by the draft's own save path
 * and its footer, so this function never sees one — the caller passes a null note target for a
 * draft, exactly as it already passes a null chart id to `useChartEditGate`.
 */
export const SharingTargetState = {
  /** A saved record whose levels have not arrived. */
  LOADING: 'loading',
  /**
   * The record could not be loaded at all — `/getChart` refused it (403), or it failed with a
   * terminal error the caller has no more specific state for (e.g. a 404 on a deleted chart). Either
   * way there is nothing to compare a request against and no amount of waiting will produce one.
   */
  UNREADABLE: 'unreadable',
  /** A record that has never been saved — a dashboard still at `UNSAVED_ID`. */
  UNSAVED: 'unsaved',
  /** A saved record with both levels in hand. */
  GOVERNED: 'governed'
} as const;
export type SharingTargetStateType = (typeof SharingTargetState)[keyof typeof SharingTargetState];

/**
 * ONE ARM PER STATE. Separate arms narrow through sequential early returns, which is also how the
 * rest of this file reads.
 */
export type SharingTarget =
  | { state: typeof SharingTargetState.LOADING }
  | { state: typeof SharingTargetState.UNREADABLE }
  | { state: typeof SharingTargetState.UNSAVED }
  | { state: typeof SharingTargetState.GOVERNED; levels: ResolvedRoleLevels };

/**
 * What the caller should DO about a request.
 *
 * `HOLD` is the only outcome that leaves the pending note in place; every other outcome means the
 * request has been dealt with and its note should be cleared.
 */
export const SharingOutcome = {
  HOLD: 'hold',
  REFUSE: 'refuse',
  NO_CHANGE: 'no-change',
  CONFIRM: 'confirm',
  WRITE: 'write'
} as const;
export type SharingOutcomeType = (typeof SharingOutcome)[keyof typeof SharingOutcome];

export type SharingDecision =
  | { outcome: typeof SharingOutcome.HOLD }
  | { outcome: typeof SharingOutcome.REFUSE; message: string }
  | { outcome: typeof SharingOutcome.NO_CHANGE }
  | { outcome: typeof SharingOutcome.CONFIRM; levels: ResolvedRoleLevels }
  | { outcome: typeof SharingOutcome.WRITE; levels: ResolvedRoleLevels };

export interface SharingRequestInput {
  /**
   * The whole request, `target` included. `target` doubles as the word for the record in a refusal
   * message — it is already `'chart'` or `'dashboard'`, which read naturally in a sentence, so there
   * is no second `noun` parameter to keep in step with it.
   */
  request: SharingRequest;
  target: SharingTarget;
  userRole: number;
  /** Whether this user may change the record. The caller's existing gate answers this. */
  canEdit: boolean;
}

// Levels in words, filtered to the ones chat can name.
//
// `assignableRoleLevels` keeps the record's `current` level even when this product does not offer it,
// so a legacy row can put an unnameable tier in this list — and a user who then asks for it produces
// a request `extractSharingRequest` rejects, which does nothing and says nothing.
//
// `roleLevelLabel` rather than the audience name: the name is a wire value between the agent and this
// code, not something to show anybody.
const listLevels = (levels: number[]): string =>
  levels
    .filter(level => levelToAudience(level) !== undefined)
    .map(level => `"${roleLevelLabel(level)}"`)
    .join(', ');

/**
 * The "what you can do instead" half of a refusal.
 *
 * ⚠️ THE LIST CAN BE EMPTY, and an earlier version of this file asserted it could not. The reasoning
 * that failed: EXTERNAL is assignable to every caller and is always nameable — true only with NO
 * ceiling. The edit column's ceiling is the REQUESTED VIEW LEVEL, so a caller whose own role equals
 * that level has exactly one assignable level, their own; and for the two tiers this product does not
 * name (INTERNAL_RESTRICTED and NOC, both real roles) that single level is then filtered out. Without
 * this branch the refusal trails off as "You can set it to ."
 *
 * An administrator is the recovery path here, as it is everywhere else in this feature: role 0
 * satisfies every level, so an administrator can always widen a record back out.
 */
const alternatives = (levels: number[]): string => {
  const named = listLevels(levels);
  return named === ''
    ? 'There is no audience you can set this to yourself — ask an administrator to change it.'
    : `You can set it to ${named}.`;
};

const refuse = (message: string): SharingDecision => ({
  outcome: SharingOutcome.REFUSE,
  message
});

/**
 * The whole sharing policy, in one pure function.
 *
 * It reproduces what `PermissionDropdown` does when a user drives it by hand — the same offered
 * lists, the same ceiling — and adds the one rule chat needs and a dialog does not: a change that
 * lets MORE people see the record is confirmed rather than written, because that is the only
 * direction a user cannot take back.
 *
 * Pure, and therefore covered by a table of cases with no React. `useSharingRequest` runs the
 * effects this names.
 */
export const decideSharingRequest = ({
  request,
  target,
  userRole,
  canEdit
}: SharingRequestInput): SharingDecision => {
  const noun = request.target;
  // Acting before the levels arrive would skip the permission check entirely, and the user would
  // meet a raw 403 — the experience FE-2 and FE-3 exist to remove.
  if (target.state === SharingTargetState.LOADING) return { outcome: SharingOutcome.HOLD };

  // The record could not be loaded at all — a refusal (403) or a terminal failure the caller treats
  // the same way (a 404 on a deleted chart), so there is nothing to change and no amount of waiting
  // will help. Refuse rather than HOLD: a held note on a permanently failed load is never cleared and
  // never acted on, so the user's request disappears in silence — the failure this feature exists to
  // remove. `canEdit` would refuse this case correctly, but LOADING short-circuits above it and never
  // reaches it. Worded to be true for both causes — it must not read as a permission problem when the
  // record is simply gone.
  if (target.state === SharingTargetState.UNREADABLE) {
    return refuse(`This ${noun} can't be opened, so you can't change who it's shared with.`);
  }

  // `useCanEditDashboard` answers TRUE for a never-saved dashboard, deliberately, so the first save
  // at a company with no dashboards is not blocked. Without this arm the request falls past the
  // permission check below, reaches WRITE, and CREATES a row as a side effect of a sharing change.
  if (target.state === SharingTargetState.UNSAVED) {
    return refuse(`Save this ${noun} first, then you can change who it's shared with.`);
  }

  if (!canEdit) {
    return refuse(
      `You don't have edit access to this ${noun}, so you can't change who it's shared with.`
    );
  }

  // The STORED pair drawn through the ceiling before anything is compared against it. A row written
  // before this rule existed can hold an edit level wider than its view level, and the comparison
  // has to be against the pair a save would actually store.
  const stored = applyCeiling(target.levels);

  // Checked in LEVELS, against the same `assignableRoleLevels` the sharing dialog builds its columns
  // from, so chat and the dialog cannot disagree about what this user may assign.
  //
  // This check runs early for a plain reason: it is the only thing that stops a refusal from listing
  // an empty set of alternatives. It is NOT load-bearing for ordering against the widening check
  // below — a caller who may edit satisfies `callerRole <= storedView`, so a view level they cannot
  // assign is always a narrowing and could never reach CONFIRM. The EDIT check's position, by
  // contrast, is load-bearing; see its own note.
  if (request.visibleTo !== undefined) {
    const allowed = assignableRoleLevels(userRole, stored.viewRoleRequired);
    if (!allowed.includes(audienceToLevel(request.visibleTo))) {
      return refuse(
        `You can't make this ${noun} visible to that audience. ${alternatives(allowed)}`
      );
    }
  }
  const view =
    request.visibleTo === undefined ? stored.viewRoleRequired : audienceToLevel(request.visibleTo);

  if (request.editableBy !== undefined) {
    const requestedEdit = audienceToLevel(request.editableBy);
    // The ceiling passed here is the REQUESTED view level, not the stored one. That is what makes
    // "visible to admins only, editable by everyone" refusable in one pass rather than written and
    // silently corrected.
    const allowed = assignableRoleLevels(userRole, stored.editRoleRequired, view);

    // TWO DIFFERENT RULES CAN REFUSE AN EDIT AUDIENCE, and they need different explanations.
    // `assignableRoleLevels` filters on the caller's own role floor AND on the ceiling. Blaming the
    // ceiling for a floor violation tells a user their NARROWER choice is "wider" than the audience
    // who can see the record, and sends them off to widen a view level that cannot fix it.
    if (requestedEdit < userRole) {
      return refuse(
        `You can't hand editing to an audience narrower than your own access, or you would not be ` +
          `able to edit this ${noun} yourself. ${alternatives(allowed)}`
      );
    }

    if (!allowed.includes(requestedEdit)) {
      return refuse(
        `Nobody can edit a ${noun} they cannot see, so that edit audience is wider than the people ` +
          `who can see this one. ${alternatives(allowed)} You can also widen who can see this ` +
          `${noun}.`
      );
    }
  }
  const edit =
    request.editableBy === undefined
      ? stored.editRoleRequired
      : audienceToLevel(request.editableBy);

  // The ceiling again, on the finished pair. The check above covers a level the user ASKED for; this
  // covers one INHERITED from the row — narrowing the view audience alone has to carry a wider
  // stored edit level down with it, which is what the dialog's own `Math.min(edit, view)` does.
  const levels = applyCeiling({ viewRoleRequired: view, editRoleRequired: edit });

  if (
    levels.viewRoleRequired === stored.viewRoleRequired &&
    levels.editRoleRequired === stored.editRoleRequired
  ) {
    return { outcome: SharingOutcome.NO_CHANGE };
  }

  // A HIGHER level admits a WIDER audience, so this comparison is the disclosure test. Widening who
  // can see a record is the one change here that cannot be undone — once a client has seen an
  // internal analyst's chart, narrowing the level again does not unsee it. Widening who can EDIT is
  // not a disclosure and needs no confirmation.
  if (levels.viewRoleRequired > stored.viewRoleRequired) {
    return { outcome: SharingOutcome.CONFIRM, levels };
  }

  return { outcome: SharingOutcome.WRITE, levels };
};

/**
 * The outcome of resolving a chat-requested audience for a record that has no stored row yet.
 *
 * `editNarrowed` exists because the ceiling can change an audience the user ASKED for, and a caller
 * with nowhere to show the resolved pair has to say so in words. A surface that renders the pair
 * before the save (the draft footer) needs no message — it is already showing the truth.
 */
export interface RequestedLevels {
  /** The pair a first save should store, or `null` when the caller may not assign what was asked. */
  levels: ResolvedRoleLevels | null;
  /** Whether the ceiling narrowed the EDIT audience the request actually named. */
  editNarrowed: boolean;
}

/**
 * A chat-requested audience as the pair a FIRST SAVE should store.
 *
 * The counterpart to `decideSharingRequest`, for the case that function cannot handle: a record with
 * no stored row to compare against — a draft chart's first save, or a new chart being appended to a
 * dashboard. There is no widening to confirm (nothing has ever been visible to anybody) and no
 * stored level to keep, so the whole job is the two rules, in order:
 *
 *  1. every audience NAMED must be one this caller may assign (`isRequestAssignable`), else `null`;
 *  2. the CEILING — nobody may edit what they cannot see — over the finished pair.
 *
 * Rule 2 is the one an assignability check alone misses, and it is why this returns a pair rather
 * than the raw request: `{visibleTo: admins_only, editableBy: everyone_with_client_access}` clears
 * rule 1 for an administrator, and sending it raw lets the backend's own `applyCeiling` store
 * something narrower than was asked for, with nothing on the client aware it happened.
 *
 * Whichever half the request left unnamed is filled from `createDefaultRoleLevels` — the pair the
 * backend would have chosen — so the ceiling runs over the same two numbers it will run over on the
 * server, and the client's promise and the stored row agree.
 */
export const resolveRequestedLevels = (
  request: SharingRequest,
  userRole: number
): RequestedLevels => {
  if (!isRequestAssignable(request, userRole)) return { levels: null, editNarrowed: false };

  const defaults = createDefaultRoleLevels(userRole);
  const requestedEdit =
    request.editableBy === undefined ? undefined : audienceToLevel(request.editableBy);
  const levels = applyCeiling({
    viewRoleRequired:
      request.visibleTo === undefined
        ? defaults.viewRoleRequired
        : audienceToLevel(request.visibleTo),
    editRoleRequired: requestedEdit ?? defaults.editRoleRequired
  });

  return {
    // Only a level the user NAMED can be "narrowed". A default edit level carried down by a narrowed
    // view is the ceiling doing its job on a value nobody asked for — reporting that would tell the
    // user their edit request changed when they never made one.
    levels,
    editNarrowed: requestedEdit !== undefined && levels.editRoleRequired !== requestedEdit
  };
};
